import { Button, Form, Switch } from 'antd';
import classNames from 'classnames';
import { useContext, useState } from 'react';

import { HowDoesItWork } from 'app/components/commons/HowDoesItWork/HowDoesItWork';
import { getHDIWDetails } from 'app/components/pages/Extranet/ExtranetInventory/Legacy/AutoInventory/AutoInventoryModal/Utils';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { AutoInventoryStepsModern } from 'app/typings/autoInventory';
import type { AutoInventoryRoomMapping } from 'app/typings/core/extranet/inventory/form/autoInventoryForm';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import type { AutoInventoryFormValues } from '../../../../types';

import styles from './ConfigStep.module.scss';
import { DeactivateModal } from './DeactivateModal/DeactivateModal';

type ConfigStepProps = {
  step: AutoInventoryStepsModern;
  children?: React.ReactNode;
  className?: string;
  handleStepChange: (step: AutoInventoryStepsModern) => void;
  onDeactivate?: () => void;
  isSaving?: boolean;
  isDisabling?: boolean;
  isNextLoading?: boolean;
};

export const ConfigStep = ({
  step,
  children,
  className = '',
  handleStepChange,
  onDeactivate,
  isSaving = false,
  isDisabling = false,
  isNextLoading = false,
}: ConfigStepProps) => {
  const { hotelInfo } = useContext(HotelContext);
  const hasAutoInventoryActivated = hotelInfo?.stockScrapperEnabled ?? false;
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const form = Form.useFormInstance();
  const roomMappings = Form.useWatch<AutoInventoryRoomMapping[]>(
    'roomMappings',
    form
  );
  const matchOTAPrice = Form.useWatch<AutoInventoryFormValues['matchOTAPrice']>(
    'matchOTAPrice',
    form
  );
  const customBar = Form.useWatch<AutoInventoryFormValues['customBar']>(
    'customBar',
    form
  );

  const footerButtons: Partial<
    Record<
      AutoInventoryStepsModern,
      {
        back?: { label: string; onClick: () => void; disabled?: boolean };
        next?: { label: string; onClick: () => void; disabled?: boolean };
      }
    >
  > = {
    [AutoInventoryStepsModern['room-mapping']]: {
      next: {
        label: 'Next',
        onClick: () => handleStepChange(AutoInventoryStepsModern.bar),
        disabled: !roomMappings?.some((rm) => rm?.scrappedRoomId),
      },
    },
    [AutoInventoryStepsModern.bar]: {
      back: {
        label: 'Back',
        onClick: () =>
          handleStepChange(AutoInventoryStepsModern['room-mapping']),
      },
      next: {
        label: 'Next',
        onClick: () => handleStepChange(AutoInventoryStepsModern.preview),
        disabled:
          matchOTAPrice === undefined ||
          (matchOTAPrice === false &&
            !(customBar?.value && customBar.value > 0)),
      },
    },
    [AutoInventoryStepsModern.preview]: {
      back: {
        label: 'Back',
        onClick: () => handleStepChange(AutoInventoryStepsModern.bar),
        disabled: isSaving || isDisabling,
      },
      next: {
        label: hotelInfo?.stockScrapperEnabled === true ? 'Update' : 'Activate',
        onClick: () => form.submit(),
      },
    },
  };

  const handleDeactivate = async () => {
    if (onDeactivate) {
      await onDeactivate();
    }

    setDeactivateModalOpen(false);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={classNames(styles.configStep, className)}>
          {children && <div className={styles.content}>{children}</div>}
        </div>
        <HowDoesItWork
          items={getHDIWDetails(step, false)}
          trackFrom="auto-inventory"
          stepFrom={step}
        />
      </div>
      <div className={styles.footer}>
        {footerButtons[step]?.back && (
          <Button
            onClick={footerButtons[step]?.back?.onClick}
            disabled={footerButtons[step]?.back?.disabled || isNextLoading}
          >
            {footerButtons[step]?.back?.label}
          </Button>
        )}

        <div className={styles.footerRight}>
          {step === AutoInventoryStepsModern.preview &&
            hasAutoInventoryActivated === false && (
              <div className={styles.overrideData}>
                Override manual data{' '}
                <Form.Item
                  name="overrideOpenings"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch size="small" disabled={isSaving || isDisabling} />
                </Form.Item>
              </div>
            )}

          {hasAutoInventoryActivated && (
            <Button
              type="default"
              className={styles.disconnectButton}
              onClick={() => {
                amplitudeTrack('Click on deactivate');
                setDeactivateModalOpen(true);
              }}
              disabled={isSaving || isDisabling}
            >
              Deactivate
            </Button>
          )}

          {footerButtons[step]?.next && (
            <Button
              type="primary"
              onClick={footerButtons[step]?.next?.onClick}
              disabled={footerButtons[step]?.next?.disabled}
              loading={
                step === AutoInventoryStepsModern.preview
                  ? isSaving
                  : isNextLoading
              }
            >
              {footerButtons[step]?.next?.label}
            </Button>
          )}
        </div>
      </div>
      <DeactivateModal
        isOpen={deactivateModalOpen}
        isDisconnectLoading={isDisabling}
        onDeactivate={handleDeactivate}
        onKeepActivated={() => setDeactivateModalOpen(false)}
        onClose={() => setDeactivateModalOpen(false)}
      />
    </>
  );
};
